import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PostPhoto } from '@reditelji/shared';
import { inOrder } from './cache';
import { api } from './client';
import { notifyApiError, type PostRow } from './posts';

export interface PhotoUploadInput {
  full: string;
  thumb: string;
  mime: string;
  width: number;
  height: number;
  captionSl?: string;
  captionEn?: string;
}

/**
 * Photo mutations for one post. Unlike the rest of the Mesta editor, these
 * write immediately — a photo is not a field you can leave half-typed, and
 * holding bytes in component state until someone presses Shrani is how uploads
 * get lost to a reload.
 */
export function usePhotoMutations(postId: string) {
  const qc = useQueryClient();
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ['posts'] }),
      qc.invalidateQueries({ queryKey: ['changelog'] }),
    ]);
  // Uploads run one after another, so waiting for a refetch between them would
  // add a round trip per photo; the strip is redrawn when the batch is done.
  const common = { onError: notifyApiError, onSettled: () => void invalidate() };

  return {
    upload: useMutation({
      mutationFn: (input: PhotoUploadInput) =>
        api<PostPhoto>(`/posts/${postId}/photos`, {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      ...common,
    }),
    // SL then EN, typed and left one after the other: in order, each answer
    // into the post's photos at once, and one refetch after the last.
    caption: useMutation({
      mutationFn: ({ id, ...body }: { id: string; captionSl?: string; captionEn?: string }) =>
        api<PostPhoto>(`/posts/${postId}/photos/${id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        }),
      ...inOrder(qc, `photo-captions:${postId}`, ['posts'], () => void invalidate()),
      onSuccess: (saved) =>
        qc.setQueryData<PostRow[]>(['posts'], (rows) =>
          rows?.map((p) =>
            p.id === postId
              ? { ...p, photos: p.photos.map((ph) => (ph.id === saved.id ? saved : ph)) }
              : p,
          ),
        ),
      onError: notifyApiError,
    }),
    reorder: useMutation({
      mutationFn: (ids: string[]) =>
        api<PostPhoto[]>(`/posts/${postId}/photos/order`, {
          method: 'PUT',
          body: JSON.stringify({ ids }),
        }),
      onError: notifyApiError,
      // Awaited, unlike the others: the caller holds an optimistic order until
      // this settles, and dropping it before the refetch lands would show the
      // old order again for a round trip.
      onSettled: invalidate,
    }),
    remove: useMutation({
      mutationFn: (id: string) =>
        api<void>(`/posts/${postId}/photos/${id}`, { method: 'DELETE' }),
      ...common,
    }),
  };
}
